import { getSessionSnapshot, sessionActions } from "@/features/auth/session";
import { API_BASE_URL } from "./config";
import { deadlineSignal } from "./deadline";
import { ApiError, toApiError } from "./errors";
import { rememberEtag } from "./etag";
import { applyIdempotencyKey } from "./idempotency";
import { markNotDispatched } from "./sentRequest";

/**
 * orval mutator. Every generated operation funnels through here:
 * - Bearer token from the in-memory session, refreshed first when it is
 *   expired or about to expire (laptop sleep, throttled background timers);
 * - automatic Idempotency-Key (exception list in idempotency.ts, matched on the
 *   resolved path before the API origin is prepended); a retry reuses it;
 * - flat error envelope → ApiError; no HTTP response → network_error;
 * - a 401 for the current access token triggers one shared refresh and a
 *   single retry; only a rejected refresh (or a second 401) ends the session;
 * - `skipAuthReset` is for endpoints that answer 401 for a wrong credential
 *   (change password): a 401 there with a token that is still good is the
 *   caller's to handle and never ends the session;
 * - response ETag remembered against the parsed body (see etag.ts).
 */
export interface RequestOptions extends RequestInit {
  /** A 401 sent with a still-valid token is a wrong credential, not a lost session. */
  skipAuthReset?: boolean;
  /**
   * A public endpoint: sent without a bearer and independent of the session
   * (no token refresh, no session-change checks).
   */
  anonymous?: boolean;
}

function bearerOf(headers: Headers): string | null {
  return headers.get("Authorization")?.replace(/^Bearer /, "") ?? null;
}

/**
 * The session changed (sign-out or another login) around this request. This
 * is a local outcome, not a server answer: status 0 keeps it out of the
 * "definitive rejection" class, so a create that may have committed keeps its
 * idempotency key for a retry.
 */
/** Longest a single API request may take before it is treated as dropped. */
export const REQUEST_TIMEOUT_MS = 60_000;

function sessionChanged(): ApiError {
  return new ApiError({
    code: "unauthorized",
    message: "",
    status: 0,
    details: { reason: "session_changed" },
  });
}

export async function request<T>(url: string, requestOptions: RequestOptions = {}): Promise<T> {
  const { skipAuthReset, anonymous, ...options } = requestOptions;
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  applyIdempotencyKey(headers, method, url);
  // Explicit credentials, or none at all (public): either way not the
  // session's, so none of the session refresh/change handling applies.
  const explicitAuth = !!anonymous || headers.has("Authorization");
  const startedIn = sessionActions.sessionGeneration();
  if (!explicitAuth) {
    try {
      await sessionActions.ensureFreshAccessToken();
      // The session changed (sign-out, or another user) while this request
      // waited: never send it with someone else's token.
      if (sessionActions.sessionGeneration() !== startedIn) {
        throw sessionChanged();
      }
    } catch (error) {
      // Nothing was dispatched: callers must not report a possible write.
      markNotDispatched(error);
      throw error;
    }
    const token = getSessionSnapshot().accessToken;
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  if (options.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const send = async () => {
    try {
      // A copy per attempt: a retry swaps the bearer without touching the first request.
      // Bounded: an API that accepts the connection but never answers must
      // not leave a write pending (and its sheet undismissable) forever. A
      // timeout is a dropped connection: the outcome is unknown.
      return await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        method,
        headers: new Headers(headers),
        signal: deadlineSignal(REQUEST_TIMEOUT_MS, options.signal),
      });
    } catch {
      // A failure that lands after the session changed belongs to the
      // previous session too: never let its callers act on the new one.
      if (!explicitAuth && sessionActions.sessionGeneration() !== startedIn) throw sessionChanged();
      throw toApiError(undefined, undefined);
    }
  };

  let response = await send();
  if (!explicitAuth && sessionActions.sessionGeneration() !== startedIn) {
    // The session changed while this request was in flight. Its result belongs
    // to the previous session; never hand it to callers that would write it
    // into the new session's caches.
    throw sessionChanged();
  }

  if (response.status === 401 && !explicitAuth) {
    const sent = bearerOf(headers);
    // With skipAuthReset, a 401 for a token that is still current and fresh
    // means the credential in the body was wrong; leave the session alone.
    const credentialRejected = skipAuthReset && sessionActions.isAccessTokenUsable(sent);
    const recovered =
      !!sent && !credentialRejected && (await sessionActions.recoverFromUnauthorized(sent, startedIn));
    const now = getSessionSnapshot();
    // A refresh can install a new token and still report failure (its
    // profile check failed for now): retry with that token all the same.
    const replaced =
      !!sent &&
      !credentialRejected &&
      sessionActions.sessionGeneration() === startedIn &&
      now.status === "authenticated" &&
      !!now.accessToken &&
      now.accessToken !== sent;
    // Checked before resending, not only after: a recovery that switched to
    // another user's session must never replay this request as them.
    if ((recovered || replaced) && sessionActions.sessionGeneration() !== startedIn) {
      throw sessionChanged();
    }
    if (recovered || replaced) {
      // Retry whenever recovery succeeded, even if the new token is byte-identical
      // (tokens issued in the same second can be); a second 401 ends the session.
      const fresh = getSessionSnapshot().accessToken;
      if (fresh) {
        headers.set("Authorization", `Bearer ${fresh}`);
        response = await send();
        if (sessionActions.sessionGeneration() !== startedIn) {
          throw sessionChanged();
        }
        if (response.status === 401 && !(skipAuthReset && sessionActions.isAccessTokenUsable(fresh))) {
          sessionActions.handleUnauthorized(fresh);
        }
      }
    } else if (
      sent &&
      !credentialRejected &&
      sessionActions.sessionGeneration() === startedIn &&
      now.status === "authenticated"
    ) {
      // The token was refused and refreshing it failed only for now (offline,
      // 5xx, 429): the session goes on, so a 401 here would be silent (auth
      // errors aren't toasted) and the user would see nothing. The server
      // refused the request, so nothing was written: report the network
      // failure it really is.
      const error = toApiError(undefined, undefined);
      markNotDispatched(error);
      throw error;
    }
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => undefined);
    // An error that belongs to a previous session must not reach the new
    // session's callers (conflict toasts, key resets, stale-edit locks).
    if (!explicitAuth && sessionActions.sessionGeneration() !== startedIn) throw sessionChanged();
    throw toApiError(errorBody, response);
  }

  const assertSameSession = () => {
    // Body download is async too: a session switch while it streams must not
    // hand this response to the new session's callers.
    if (!explicitAuth && sessionActions.sessionGeneration() !== startedIn) {
      throw sessionChanged();
    }
  };
  if (response.status === 204 || response.status === 304) {
    assertSameSession();
    return undefined as T;
  }
  let text: string;
  try {
    text = await response.text();
  } catch {
    // The body was cut off (deadline or dropped connection): like a failed
    // request, the outcome is unknown.
    assertSameSession();
    throw toApiError(undefined, undefined);
  }
  assertSameSession();
  // A JSON response must have a body (204/304 are handled above): an empty
  // one was cut off, so the outcome is unknown.
  if (!text && (response.headers.get("Content-Type") ?? "").includes("json"))
    throw toApiError(undefined, undefined);
  if (!text) return undefined as T;
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    // Declared JSON but unreadable: the operation may still have happened
    // (e.g. a minted token): an unknown outcome, never a "success" string.
    if ((response.headers.get("Content-Type") ?? "").includes("json")) throw toApiError(undefined, undefined);
    body = text;
  }
  rememberEtag(body, response);
  return body as T;
}

export default request;
